import { FormEvent } from "react"
import { Head, Link, router, useForm } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"
import { TableCard } from "@/components/blocks/TableCard"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/catalyst/table"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { PROGRAM_ICONS } from "@/lib/program-icons"

type Program = {
  id: number
  name: string
  slug: string
  description: string | null
  active: boolean
  auto_enroll: boolean
  landing_enabled: boolean
  signup_mode: "code" | "open"
  tagline: string | null
  headline: string | null
  subheadline: string | null
  cta_label: string | null
  highlight_title: string | null
  highlight_body: string | null
  features: Feature[]
}

type Feature = { icon: string; title: string; body: string }

type Code = {
  id: number
  code: string
  label: string | null
  status: "active" | "disabled" | "expired" | "used_up"
  max_uses: number | null
  uses_count: number
  access_days: number | null
  expires_at: string | null
  redeemers: string[]
}

type Instructor = { id: number; email: string; name: string | null }

const STATUS_LABELS: Record<Code["status"], string> = {
  active: "Active",
  disabled: "Disabled",
  expired: "Expired",
  used_up: "Used up",
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
}

export default function AdminProgramShow({
  program,
  codes,
  instructors,
  member_count,
}: {
  program: Program
  codes: Code[]
  instructors: Instructor[]
  member_count: number
}) {
  const details = useForm({
    name: program.name,
    slug: program.slug,
    description: program.description ?? "",
    active: program.active,
    auto_enroll: program.auto_enroll,
    landing_enabled: program.landing_enabled,
    signup_mode: program.signup_mode,
    tagline: program.tagline ?? "",
    headline: program.headline ?? "",
    subheadline: program.subheadline ?? "",
    cta_label: program.cta_label ?? "",
    highlight_title: program.highlight_title ?? "",
    highlight_body: program.highlight_body ?? "",
    features: program.features,
  })

  const setFeature = (index: number, change: Partial<Feature>) =>
    details.setData(
      "features",
      details.data.features.map((f, i) => (i === index ? { ...f, ...change } : f)),
    )
  const removeFeature = (index: number) =>
    details.setData(
      "features",
      details.data.features.filter((_, i) => i !== index),
    )
  const addFeature = () => details.setData("features", [...details.data.features, { icon: "check", title: "", body: "" }])
  const newCode = useForm({ label: "", max_uses: "", access_days: "", expires_on: "" })

  const saveDetails = (e: FormEvent) => {
    e.preventDefault()
    details.patch(`/admin/programs/${program.id}`, { preserveScroll: true })
  }

  const createCode = (e: FormEvent) => {
    e.preventDefault()
    newCode.post(`/admin/programs/${program.id}/access_codes`, { preserveScroll: true, onSuccess: () => newCode.reset() })
  }

  const toggleCode = (code: Code) =>
    router.patch(`/admin/access_codes/${code.id}/${code.status === "disabled" ? "enable" : "disable"}`, {}, { preserveScroll: true })

  const description = `Settings, access codes, and instructors for ${program.name}.`

  return (
    <>
      <Head title={program.name}>
        <meta name="description" content={description} />
        <meta property="og:title" content={program.name} />
        <meta property="og:description" content={description} />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>{program.name}</h1>
              <p className="mt-1">
                {member_count} current {member_count === 1 ? "member" : "members"}
              </p>
            </div>
            <Link href="/admin/programs" className="text-sm text-ink-muted no-underline hover:text-ink-display">
              Back to programs
            </Link>
          </div>
        </div>

        <form onSubmit={saveDetails} className="mt-8 max-w-md space-y-4">
          <h2>Details</h2>
          <div className="space-y-2">
            <label htmlFor="program-name">Name</label>
            <Input id="program-name" required value={details.data.name} onChange={(e) => details.setData("name", e.target.value)} />
            {details.errors.name && <p className="text-xs text-danger-display">{details.errors.name}</p>}
          </div>
          <div className="space-y-2">
            <label htmlFor="program-slug">URL name</label>
            <Input id="program-slug" required value={details.data.slug} onChange={(e) => details.setData("slug", e.target.value)} />
            {details.errors.slug && <p className="text-xs text-danger-display">{details.errors.slug}</p>}
          </div>
          <div className="space-y-2">
            <label htmlFor="program-description">Description</label>
            <textarea
              id="program-description"
              className="form-control form-control-textarea"
              value={details.data.description}
              onChange={(e) => details.setData("description", e.target.value)}
            />
          </div>
          <label className="flex cursor-pointer items-start gap-2 font-normal text-ink-body">
            <Checkbox
              className="mt-0.5"
              checked={details.data.active}
              onChange={(e) => details.setData("active", e.target.checked)}
            />
            <span>Active. Inactive programs are hidden from members.</span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 font-normal text-ink-body">
            <Checkbox
              className="mt-0.5"
              checked={details.data.auto_enroll}
              onChange={(e) => details.setData("auto_enroll", e.target.checked)}
            />
            <span>Add every new account to this program automatically.</span>
          </label>
          <Button type="submit" disabled={details.processing}>
            Save details
          </Button>
        </form>

        <form onSubmit={saveDetails} className="mt-12 max-w-2xl space-y-4" aria-label="Landing page">
          <h2>Landing page</h2>
          <p>
            A public page at <code>/p/{program.slug}</code> that explains the program and sends people to sign up.
            {program.landing_enabled && (
              <>
                {" "}
                <a href={`/p/${program.slug}`} target="_blank" rel="noopener noreferrer">
                  View it
                </a>
                .
              </>
            )}
          </p>
          <label className="flex cursor-pointer items-start gap-2 font-normal text-ink-body">
            <Checkbox
              className="mt-0.5"
              checked={details.data.landing_enabled}
              onChange={(e) => details.setData("landing_enabled", e.target.checked)}
            />
            <span>Publish the landing page. It needs a headline.</span>
          </label>
          <div className="space-y-2">
            <label htmlFor="landing-signup-mode">Signing up from this page</label>
            <Select
              id="landing-signup-mode"
              value={details.data.signup_mode}
              onChange={(e) => details.setData("signup_mode", e.target.value as "code" | "open")}
            >
              <option value="code">Requires an access code</option>
              <option value="open">Unlocks the program for everyone</option>
            </Select>
          </div>
          <div className="space-y-2">
            <label htmlFor="landing-tagline">Tagline</label>
            <Input id="landing-tagline" value={details.data.tagline} onChange={(e) => details.setData("tagline", e.target.value)} />
          </div>
          <div className="space-y-2">
            <label htmlFor="landing-headline">Headline</label>
            <Input id="landing-headline" value={details.data.headline} onChange={(e) => details.setData("headline", e.target.value)} />
            {details.errors.headline && <p className="text-xs text-danger-display">{details.errors.headline}</p>}
          </div>
          <div className="space-y-2">
            <label htmlFor="landing-subheadline">Introduction</label>
            <textarea
              id="landing-subheadline"
              className="form-control form-control-textarea"
              value={details.data.subheadline}
              onChange={(e) => details.setData("subheadline", e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="landing-cta">Button text</label>
            <Input id="landing-cta" placeholder="Get started" value={details.data.cta_label} onChange={(e) => details.setData("cta_label", e.target.value)} />
          </div>

          <fieldset className="space-y-3">
            <legend>Feature cards</legend>
            {details.data.features.map((feature, index) => (
              <div key={index} className="space-y-2 rounded-lg border border-hairline p-4">
                <div className="grid gap-3 sm:grid-cols-[10rem_1fr]">
                  <Select
                    aria-label={`Feature ${index + 1} icon`}
                    value={feature.icon}
                    onChange={(e) => setFeature(index, { icon: e.target.value })}
                  >
                    {PROGRAM_ICONS.map((icon) => (
                      <option key={icon.value} value={icon.value}>
                        {icon.label}
                      </option>
                    ))}
                  </Select>
                  <Input
                    aria-label={`Feature ${index + 1} title`}
                    placeholder="Title"
                    value={feature.title}
                    onChange={(e) => setFeature(index, { title: e.target.value })}
                  />
                </div>
                <textarea
                  aria-label={`Feature ${index + 1} description`}
                  className="form-control form-control-textarea"
                  placeholder="Description"
                  value={feature.body}
                  onChange={(e) => setFeature(index, { body: e.target.value })}
                />
                <Button type="button" size="sm" variant="ghost" onClick={() => removeFeature(index)}>
                  Remove feature {index + 1}
                </Button>
              </div>
            ))}
            {details.data.features.length < 9 && (
              <Button type="button" size="sm" variant="secondary" onClick={addFeature}>
                Add a feature
              </Button>
            )}
          </fieldset>

          <div className="space-y-2">
            <label htmlFor="landing-highlight-title">Highlighted note: title (optional)</label>
            <Input
              id="landing-highlight-title"
              value={details.data.highlight_title}
              onChange={(e) => details.setData("highlight_title", e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="landing-highlight-body">Highlighted note: text</label>
            <textarea
              id="landing-highlight-body"
              className="form-control form-control-textarea"
              value={details.data.highlight_body}
              onChange={(e) => details.setData("highlight_body", e.target.value)}
            />
          </div>
          <Button type="submit" disabled={details.processing}>
            Save landing page
          </Button>
        </form>

        <section className="mt-12" aria-label="Access codes">
          <h2>Access codes</h2>
          <form onSubmit={createCode} className="mt-4 grid max-w-2xl gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <label htmlFor="code-label">Label (optional)</label>
              <Input
                id="code-label"
                placeholder="Fall 2026 cohort"
                value={newCode.data.label}
                onChange={(e) => newCode.setData("label", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="code-max-uses">Number of uses</label>
              <Input
                id="code-max-uses"
                type="number"
                min={1}
                placeholder="Unlimited"
                value={newCode.data.max_uses}
                onChange={(e) => newCode.setData("max_uses", e.target.value)}
              />
              <p className="text-xs text-ink-muted">Leave empty for unlimited. Use 1 for a single-use code.</p>
            </div>
            <div className="space-y-2">
              <label htmlFor="code-access-days">Access lasts (days)</label>
              <Input
                id="code-access-days"
                type="number"
                min={1}
                placeholder="Permanent"
                value={newCode.data.access_days}
                onChange={(e) => newCode.setData("access_days", e.target.value)}
              />
              <p className="text-xs text-ink-muted">Counted from the day it&rsquo;s redeemed. Empty means permanent.</p>
            </div>
            <div className="space-y-2">
              <label htmlFor="code-expires-on">Redeem by (optional)</label>
              <Input
                id="code-expires-on"
                type="date"
                value={newCode.data.expires_on}
                onChange={(e) => newCode.setData("expires_on", e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button type="submit" disabled={newCode.processing}>
                Create code
              </Button>
            </div>
          </form>

          {codes.length === 0 ? (
            <p className="callout mt-6">No codes yet.</p>
          ) : (
            <div className="mt-6">
              <TableCard>
                <Table bleed className="[--gutter:--spacing(5)] [&_tbody_tr:last-child_td]:border-b-0">
                  <TableHead>
                    <TableRow>
                      <TableHeader>Code</TableHeader>
                      <TableHeader>Status</TableHeader>
                      <TableHeader>Usage</TableHeader>
                      <TableHeader className="relative w-0">
                        <span className="sr-only">Actions</span>
                      </TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {codes.map((code) => (
                      <TableRow key={code.id}>
                        <TableCell className="whitespace-normal">
                          <code className="text-base font-semibold text-ink-display">{code.code}</code>
                          {code.label && <div className="text-xs text-ink-muted">{code.label}</div>}
                        </TableCell>
                        <TableCell>
                          <Badge tone={code.status === "active" ? "accent" : "muted"}>{STATUS_LABELS[code.status]}</Badge>
                        </TableCell>
                        <TableCell className="whitespace-normal text-sm text-ink-muted">
                          <div>
                            {code.uses_count} of {code.max_uses ?? "unlimited"} used
                          </div>
                          <div>
                            {code.access_days ? `${code.access_days} days of access` : "permanent access"}
                            {code.expires_at ? ` · redeem by ${formatDate(code.expires_at)}` : ""}
                          </div>
                          {code.redeemers.length > 0 && <div>Used by {code.redeemers.join(", ")}</div>}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button type="button" size="sm" variant="secondary" onClick={() => toggleCode(code)}>
                            {code.status === "disabled" ? "Enable" : "Disable"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableCard>
            </div>
          )}
        </section>

        <section className="mt-12" aria-label="Instructors">
          <h2>Instructors</h2>
          {instructors.length === 0 ? (
            <p className="mt-2">No instructors are assigned. Assign them from a user&rsquo;s page.</p>
          ) : (
            <ul className="mt-3 list-none space-y-2 pl-0">
              {instructors.map((user) => (
                <li key={user.id}>
                  <Link href={`/admin/users/${user.id}`}>{user.name ?? user.email}</Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </AdminShell>
    </>
  )
}
